// PATH: src/components/chapters/reviewStatus.ts
/**
 * Relecture humaine des chapitres : libellés, tons et filtre (logique pure,
 * testée). Les états viennent du serveur (`review_status`) ; un chapitre sans
 * état (serveur ancien) est « relecture non requise ».
 */
import type { PillTone } from "../ui/StatusPill";
import type { ReviewEvent, ReviewStatus } from "../../models/domain";

export const REVIEW_STATUSES: ReviewStatus[] = ["a_relire", "a_corriger", "validee", "non_requise"];

/** Badge d'un chapitre. */
export const REVIEW_LABELS: Record<ReviewStatus, string> = {
  a_relire: "À relire",
  a_corriger: "À corriger",
  validee: "Validé",
  non_requise: "Relecture non requise",
};

export const REVIEW_TONES: Record<ReviewStatus, PillTone> = {
  a_relire: "warning",
  a_corriger: "error",
  validee: "success",
  non_requise: "neutral",
};

/** Filtre « Relecture » de la liste : `all` ou un état. */
export type ReviewFilter = "all" | ReviewStatus;

export const REVIEW_FILTER_LABELS: Record<ReviewFilter, string> = {
  all: "Tous les chapitres",
  a_relire: "À relire",
  a_corriger: "À corriger",
  validee: "Validés",
  non_requise: "Relecture non requise",
};

export const asReviewStatus = (raw: string | null | undefined): ReviewStatus =>
  REVIEW_STATUSES.includes(raw as ReviewStatus) ? (raw as ReviewStatus) : "non_requise";

export const asReviewFilter = (raw: string | null): ReviewFilter =>
  raw && REVIEW_STATUSES.includes(raw as ReviewStatus) ? (raw as ReviewStatus) : "all";

export const matchesReview = (status: string | null | undefined, filter: ReviewFilter): boolean =>
  filter === "all" || asReviewStatus(status) === filter;

/** Le badge mérite d'être affiché : une relecture est en jeu. */
export const showReviewPill = (status: string | null | undefined): boolean =>
  asReviewStatus(status) !== "non_requise";

/** Comptes par état (liste chargée). */
export const reviewCounts = (rows: { review_status?: string | null }[]): Record<ReviewStatus, number> => {
  const out: Record<ReviewStatus, number> = { a_relire: 0, a_corriger: 0, validee: 0, non_requise: 0 };
  rows.forEach((r) => {
    out[asReviewStatus(r.review_status)] += 1;
  });
  return out;
};

/** Auteur lisible d'un changement d'état. */
export const formatReviewActor = (actor: string | null | undefined, name?: string | null): string => {
  if (name && name.trim()) return name.trim();
  if (!actor) return "—";
  if (actor === "import") return "Import du chapitre";
  if (actor === "editor:internal") return "Service interne";
  const m = /^editor:(.+)$/.exec(actor);
  return m ? `Utilisateur n° ${m[1]}` : actor;
};

/** Phrase d'historique : « Validé », « Importé : à relire »… */
export const formatReviewEvent = (e: Pick<ReviewEvent, "status" | "actor">): string => {
  if (e.actor === "import") {
    if (e.status === "a_relire") return "Nouvelle version importée : à relire";
    if (e.status === "non_requise") return "Nouvelle version importée : relecture non requise";
    return `Nouvelle version importée : ${REVIEW_LABELS[e.status].toLowerCase()}`;
  }
  if (e.status === "validee") return "Relecture validée";
  if (e.status === "a_corriger") return "Marqué à corriger";
  return REVIEW_LABELS[e.status] ?? e.status;
};

/** Commentaire obligatoire pour « À corriger » ; null si la décision peut partir. */
export const reviewDecisionError = (status: "validee" | "a_corriger", comment: string): string | null =>
  status === "a_corriger" && !comment.trim() ? "Dites ce qui est à corriger : le commentaire est obligatoire." : null;
